"use client";

import { createRestDataProviderClient } from "@providers/data-provider/rest-data-provider.client";
import { isFilterValueExist, parseFilter, transformQueryParamsWithoutLocale } from "@shared/utils";
import { ComboboxOption as BaseComboboxOption, Checkbox, SearchInput } from "@ubud-ui/react-ui-kit";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import qs from "qs";
import { useState } from "react";

type Platform = {
  id: string;
  name: string;
  totalVacancies: number;
};

type PlatformResponse = {
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  };
  data: Platform[];
};

type ComboboxOption = BaseComboboxOption & {
  totalVacancies: number | null;
};

const FILTER_KEY = "platforms";

export function PlatformFilter({ path }: { path: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const restDataProviderClient = createRestDataProviderClient();

  const defaultOption: ComboboxOption = {
    label: "Karirhub",
    totalVacancies: null,
    value: "internal",
  };

  const [keyword, setKeyword] = useState<string>("");
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<PlatformResponse>({
    queryKey: ["vacancy-platform-filter", keyword],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale("/karirhub/vacancy/v2/platforms", {
          keyword,
          limit: 50,
          is_available: 1,
          page: pageParam,
        }),
        method: "GET",
      });

      return response;
    },
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.meta.currentPage;
      const lastPageNumber = lastPage.meta.lastPage;

      if (currentPage < lastPageNumber) {
        return currentPage + 1;
      }

      return undefined;
    },
  });
  const options: ComboboxOption[] = data
    ? [
        defaultOption,
        ...data.pages.flatMap((page) =>
          page.data.map((it) => ({
            value: it.id,
            label: it.name,
            totalVacancies: it.totalVacancies,
          })),
        ),
      ]
    : [defaultOption];

  const handleSearch = (keyword: string) => {
    setKeyword(keyword);
  };

  const handleFilter = (option: ComboboxOption, checked: boolean) => {
    const paramsString = searchParams.toString();

    const value = {
      id: option.value,
      label: option.label,
    };

    const params = parseFilter(FILTER_KEY, value, qs.parse(paramsString), checked);

    router.push(`${path}?${qs.stringify(params, { skipNulls: true })}`, {
      scroll: false,
    });
  };

  const isChecked = (value: string) => {
    const params = qs.parse(searchParams.toString());

    return isFilterValueExist(params, FILTER_KEY, value);
  };

  return (
    <>
      <div className="ml-px mt-px">
        <SearchInput placeholder="Cari sumber" value={keyword} onSearch={handleSearch} />
      </div>
      {options && options.length > 0 ? (
        <div className="space-y-3 pt-5 pb-2">
          {options.map((option, index) => {
            return (
              <div className="flex items-start gap-3" key={option.value + "-" + index}>
                {isChecked(option.value)}
                <Checkbox
                  className="mt-1"
                  id={option.value + "-" + index}
                  onCheckedChange={(checked) => handleFilter(option, checked as boolean)}
                  checked={isChecked(option.value)}
                />
                <label htmlFor={option.value + "-" + index}>
                  {option.label}
                  {/* {option.totalVacancies !== null && ` (${option.totalVacancies || 0})`} */}
                </label>
              </div>
            );
          })}
          {hasNextPage && (
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="text-sm font-medium text-primary hover:underline pt-3"
            >
              {isFetchingNextPage ? "Memuat..." : "Lihat lebih banyak"}
            </button>
          )}
        </div>
      ) : (
        <div className="text-center font-bold pt-5 pb-2">
          {!keyword ? "Ketik untuk mencari sumber lowongan" : "Sumber lowongan tidak ditemukan"}
        </div>
      )}
    </>
  );
}
